/**
 * Resource validation and utility functions.
 *
 * Shared domain logic for resource/skill operations used by both
 * the UI setup endpoints (`/api/setup/resources/*`) and the CI push
 * endpoint (`/api/ci/push`). Follows the agent-settings.ts pattern:
 * pure functions with no IO — callers handle persistence.
 */

import { slugifyKebab } from './slugify';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum file size for individual resource file uploads (10 MB). */
export const MAX_RESOURCE_FILE_SIZE = 10 * 1024 * 1024;

/** Known binary file extensions (lowercase, no dot). */
export const BINARY_EXTENSIONS = new Set([
  'pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico',
  'mp3', 'mp4', 'wav', 'zip', 'tar', 'gz',
  'woff', 'woff2', 'ttf', 'eot',
]);

// ---------------------------------------------------------------------------
// Skill name
// ---------------------------------------------------------------------------

/**
 * Generate a valid skill_name slug from a display name.
 *
 * Thin wrapper over the canonical `slugifyKebab` producer (NFKD-normalized,
 * diacritics folded, 64-char cap). Kept as a named export because callers refer
 * to it by intent ("skill name"); the algorithm lives in one place so the UI
 * preview, CLI push, and backend persistence can never disagree.
 */
export function slugifySkillName(name: string): string {
  return slugifyKebab(name, 64);
}

/**
 * Validate a skill_name against the platform specification.
 * Returns an array of error strings (empty = valid).
 *
 * Rules: 2-64 chars, lowercase alphanumeric + hyphens,
 * cannot start/end with hyphen, no consecutive hyphens.
 */
export function validateSkillName(name: string): string[] {
  const errors: string[] = [];
  if (name.length < 2 || name.length > 64) {
    errors.push('skill_name must be 2-64 characters');
  }
  const pattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
  if (!pattern.test(name)) {
    errors.push(
      'skill_name must contain only lowercase letters, numbers, and hyphens, and cannot start or end with a hyphen',
    );
  }
  if (name.includes('--')) {
    errors.push('skill_name cannot contain consecutive hyphens');
  }
  return errors;
}

// ---------------------------------------------------------------------------
// SKILL.md validation
// ---------------------------------------------------------------------------

/**
 * Validate SKILL.md content (frontmatter format and required fields).
 * Used during both individual file upload (UI) and bulk push (CI).
 */
export function validateSkillMd(
  content: string,
): { valid: boolean; errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!content.startsWith('---')) {
    errors.push('SKILL.md must start with YAML frontmatter (---)');
    return { valid: false, errors, warnings };
  }

  const frontmatterEnd = content.indexOf('---', 3);
  if (frontmatterEnd === -1) {
    errors.push('SKILL.md frontmatter is not properly closed');
    return { valid: false, errors, warnings };
  }

  const frontmatter = content.substring(3, frontmatterEnd).trim();

  if (!frontmatter.includes('name:')) {
    errors.push('SKILL.md frontmatter must include a name field');
  }
  if (!frontmatter.includes('description:')) {
    warnings.push('SKILL.md frontmatter should include a description field');
  }

  return { valid: errors.length === 0, errors, warnings };
}

// ---------------------------------------------------------------------------
// Path safety
// ---------------------------------------------------------------------------

/**
 * Check whether a file path is safe (no traversal escaping root, no absolute).
 * Uses depth tracking to allow `a/b/../c.txt` but reject `../../etc/passwd`.
 */
export function isPathSafe(filePath: string): boolean {
  if (filePath.startsWith('/')) return false;
  const segments = filePath.split('/');
  let depth = 0;
  for (const segment of segments) {
    if (segment === '..') {
      depth--;
      if (depth < 0) return false;
    } else if (segment !== '.' && segment !== '') {
      depth++;
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// File type detection
// ---------------------------------------------------------------------------

/**
 * Check whether a file path has a known binary extension.
 * Case-insensitive.
 */
export function isBinaryExtension(filePath: string): boolean {
  const ext = filePath.split('.').pop()?.toLowerCase() || '';
  return BINARY_EXTENSIONS.has(ext);
}

// ---------------------------------------------------------------------------
// File metadata
// ---------------------------------------------------------------------------

/** Canonical metadata shape stored in HubDO `resource_file.metadata`. */
export interface FileMetadata {
  file_name: string;
  mime_type: string;
  file_size: number;
  file_hash?: string | null;
  bucket: 'resource';
}

/**
 * Build a canonical FileMetadata object for a resource file.
 * File content lives in R2 — metadata only stores descriptive fields.
 * Omits `file_hash` when null to keep metadata compact.
 */
export function buildFileMetadata(opts: {
  fileName: string;
  filePath: string;
  mimeType: string;
  fileSize: number;
  fileHash?: string | null;
}): FileMetadata {
  const fileHash = opts.fileHash ?? null;

  const meta: FileMetadata = {
    file_name: opts.fileName,
    mime_type: opts.mimeType,
    file_size: opts.fileSize,
    file_hash: fileHash,
    bucket: 'resource',
  };

  // Omit null fields to keep metadata compact
  if (meta.file_hash === null) delete (meta as unknown as Record<string, unknown>).file_hash;

  return meta;
}

/**
 * The keys of a resource file's metadata that describe its stored object (`buildFileMetadata`'s,
 * and a legacy `r2_path`). HubDO moves them into its `file` table when it inserts a resource file,
 * and keeps them in `metadata` as given when it updates one.
 */
export const FILE_METADATA_KEYS: ReadonlySet<string> = new Set([
  'file_name',
  'mime_type',
  'bucket',
  'file_size',
  'file_hash',
  'r2_path',
]);
